import {
  Alert,
  Avatar,
  Box,
  Button,
  ButtonBase,
  Card,
  Skeleton,
  Snackbar,
  TextField,
  Typography,
} from "@mui/material";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import { useEffect, useMemo, useRef, useState } from "react";
import { useCurrentProfile } from "../../../../../ContextWrapper";
import { get, patch } from "../../../../../api/api";
import { submitPracticeDetail } from "../../../../../api/customerSetup";

const Field = ({ label, value }) => {
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "4px", flex: 1 }}>
      <Typography
        sx={{
          fontFamily: "Rethink Sans",
          fontWeight: 400,
          fontSize: "14px",
          lineHeight: "143%",
          letterSpacing: "0.17px",
          color: "rgba(0,0,0,0.6)",
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          fontFamily: "Rethink Sans",
          fontWeight: 500,
          fontSize: "15px",
          lineHeight: "150%",
          letterSpacing: "0.15px",
          color: "rgba(0,0,0,0.87)",
          wordBreak: "break-word",
        }}
      >
        {value || "—"}
      </Typography>
    </Box>
  );
};

export default function UserDetails({ onEdit, onLoadingChange } = {}) {
  const { profile } = useCurrentProfile();
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState(() => ({
    firstName: "",
    lastName: "",
    email: "",
    address1: "",
    address2: "",
    city: "",
    zip: "",
  }));
  const [photoFile, setPhotoFile] = useState(null);
  const photoFilePickerRef = useRef(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMsg, setSnackbarMsg] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  useEffect(() => {
    if (!profile?.id) return;
    let active = true;
    setLoading(true);

    void (async () => {
      try {
        const fullProfile = await get(`/api/v2/profiles/${profile.id}/`);
        if (!active) return;
        setDetails(fullProfile || null);
      } catch (err) {
        console.error("Failed loading profile details:", err);
      } finally {
        if (active) {
          setLoading(false);
          onLoadingChange?.(false);
        }
      }
    })();

    return () => {
      active = false;
    };
  }, [profile?.id, onLoadingChange]);

  const user = details?.user || {};
  const customers = Array.isArray(details?.customers) ? details.customers : [];
  // PracticeDetail endpoint explicitly blocks updates to demo customers.
  // Prefer showing/editing a non-demo customer when available.
  const nonDemoCustomers = customers.filter((c) => !c?.is_demo);
  const firstNonDemoActive =
    nonDemoCustomers.find((c) => c?.is_active) || nonDemoCustomers[0] || null;

  const firstActiveCustomer =
    firstNonDemoActive ||
    customers.find((c) => c?.is_active) ||
    customers[0] ||
    null;
  const customerId = firstActiveCustomer?.id || null;
  const customerName = (firstActiveCustomer?.name || "").trim();
  const customerAddress1 = (firstActiveCustomer?.address1 || firstActiveCustomer?.address || "").trim();
  const customerAddress2 = (firstActiveCustomer?.address2 || "").trim();
  const address = [customerAddress1, customerAddress2].filter(Boolean).join(", ");
  const city = (firstActiveCustomer?.city || "").trim();
  const zipCode = (firstActiveCustomer?.zip_code || firstActiveCustomer?.zip || "").trim();
  const profilePictureUrl = (details?.profile_picture || "").trim();

  const profilePictureSrc = useMemo(() => {
    if (!profilePictureUrl) return "";
    let path = profilePictureUrl;
    if (process.env.NODE_ENV === "development" && profilePictureUrl.startsWith("http")) {
      try {
        const parsed = new URL(profilePictureUrl);
        const host = parsed.hostname.toLowerCase();
        const isDevBackend = host === "backend" || host === "localhost" || host === "127.0.0.1";
        if (isDevBackend) {
          path = parsed.pathname;
        }
      } catch {
        const match = profilePictureUrl.match(
          /^https?:\/\/(backend|localhost|127\.0\.0\.1)(?::\d+)?(\/.*)$/
        );
        if (match) path = match[2];
      }
    }
    const version = (details?.modified_on || "").trim();
    if (!version) return path;
    const join = path.includes("?") ? "&" : "?";
    return `${path}${join}v=${encodeURIComponent(version)}`;
  }, [profilePictureUrl, details?.modified_on]);

  const photoPreviewUrl = useMemo(() => {
    if (!photoFile) return "";
    return URL.createObjectURL(photoFile);
  }, [photoFile]);

  useEffect(() => {
    return () => {
      if (photoPreviewUrl) URL.revokeObjectURL(photoPreviewUrl);
    };
  }, [photoPreviewUrl]);

  const inputSx = useMemo(
    () => ({
      fontFamily: "Rethink Sans",
      "& .MuiOutlinedInput-root": {
        height: "40px",
        borderRadius: "8px",
        "& fieldset": { borderColor: "#E0E0E0" },
        "&.Mui-focused fieldset": { borderColor: "primary.main" },
      },
      "& input": { padding: "8px 12px", fontSize: "14px" },
    }),
    []
  );

  const startEditing = () => {
    setDraft({
      firstName: (user?.first_name || "").trim(),
      lastName: (user?.last_name || "").trim(),
      email: (user?.email || "").trim(),
      address1: customerAddress1,
      address2: customerAddress2,
      city,
      zip: zipCode,
    });
    setPhotoFile(null);
    setIsEditing(true);
    onEdit?.();
  };

  const cancelEditing = () => {
    setIsEditing(false);
    setPhotoFile(null);
  };

  const saveChanges = async () => {
    if (!profile?.id) return;
    if (saving) return;

    setSaving(true);
    try {
      // Backend explicitly blocks updates to demo customers. If the current selected
      // customer is a demo (or there isn't one), allow the backend to create/update
      // a non-demo customer by sending `null` for `customer_id`.
      const customerIdForSave = firstActiveCustomer?.is_demo ? null : customerId;

      const profileForm = new FormData();
      profileForm.append("first_name", draft.firstName);
      profileForm.append("last_name", draft.lastName);
      profileForm.append("email", draft.email);
      if (photoFile) {
        profileForm.append("profile_picture", photoFile);
      }

      await patch(`/api/v2/profiles/${profile.id}/`, profileForm);

      await submitPracticeDetail(
        {
          businessName: customerName,
          practiceName: customerName,
          address1: draft.address1,
          address2: draft.address2,
          city: draft.city,
          zip: draft.zip,
        },
        customerIdForSave
      );

      const fullProfile = await get(`/api/v2/profiles/${profile.id}/`);
      setDetails(fullProfile || null);
      window.dispatchEvent(new Event("profile-updated"));
      setIsEditing(false);
      setPhotoFile(null);
      setSnackbarMsg("Profile updated.");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
    } catch (err) {
      console.error("Failed saving profile details:", err);
      const message = err?.response?.data?.error || err?.message || "";
      if (String(message).includes("Demo customer cannot be modified")) {
        setSnackbarMsg("Demo customer cannot be modified.");
      } else {
        setSnackbarMsg("Failed to save changes. Please try again.");
      }
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Card
        sx={{
          borderRadius: "12px",
          border: "1px solid #E0E0E0",
          boxShadow: "0px 1px 4px 0px rgba(0,0,0,0.04)",
          overflow: "hidden",
          width: "100%",
        }}
      >
        {/* Header skeleton */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            px: "24px",
            py: "14px",
            borderBottom: "1px solid rgba(0,0,0,0.08)",
            backgroundColor: "#fff",
          }}
        >
          <Skeleton variant="circular" width={20} height={20} />
          <Skeleton variant="text" width={120} height={28} sx={{ flex: 1 }} />
          <Skeleton variant="rounded" width={60} height={28} />
        </Box>

        {/* Fields skeleton */}
        <Box
          sx={{
            px: "24px",
            pt: "20px",
            pb: "24px",
            backgroundColor: "#fff",
            display: "flex",
            flexDirection: "column",
            gap: "24px",
          }}
        >
          {[0, 1, 2].map((row) => (
            <Box key={row} sx={{ display: "flex", gap: "24px", width: "100%", flexWrap: "wrap" }}>
              {[0, 1].map((col) => (
                <Box key={col} sx={{ display: "flex", flexDirection: "column", gap: "6px", flex: 1, minWidth: { xs: "100%", md: 0 } }}>
                  <Skeleton variant="text" width={80} height={18} />
                  <Skeleton variant="text" width="70%" height={22} />
                </Box>
              ))}
            </Box>
          ))}

          {/* Photo skeleton */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            <Skeleton variant="text" width={80} height={18} />
            <Skeleton variant="circular" width={72} height={72} />
          </Box>
        </Box>
      </Card>
    );
  }

  return (
    <Card
      sx={{
        borderRadius: "12px",
        border: "1px solid #E0E0E0",
        boxShadow: "0px 1px 4px 0px rgba(0,0,0,0.04)",
        overflow: "hidden",
        width: "100%",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          px: "24px",
          py: "14px",
          borderBottom: "1px solid rgba(0,0,0,0.08)",
          backgroundColor: "#fff",
        }}
      >
        <BusinessOutlinedIcon sx={{ color: "#536DEC", fontSize: 20 }} />
        <Typography
          sx={{
            fontFamily: "Rethink Sans",
            fontWeight: 600,
            fontSize: "18px",
            lineHeight: "175%",
            letterSpacing: "0.15px",
            color: "rgba(0,0,0,0.87)",
            flex: 1,
          }}
        >
          My Details
        </Typography>

        {!isEditing && (
          <ButtonBase
            onClick={startEditing}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              px: "5px",
              py: "4px",
              borderRadius: "4px",
            }}
          >
            <EditOutlinedIcon sx={{ color: "#536DEC", fontSize: 20 }} />
            <Typography
              sx={{
                fontFamily: "Rethink Sans",
                fontWeight: 500,
                fontSize: "14px",
                lineHeight: "24px",
                letterSpacing: "0.4px",
                color: "#536DEC",
                textTransform: "capitalize",
              }}
            >
              Edit
            </Typography>
          </ButtonBase>
        )}
      </Box>

      <Box
        sx={{
          px: "24px",
          pt: "20px",
          pb: "24px",
          backgroundColor: "#fff",
          display: "flex",
          flexDirection: "column",
          gap: "24px",
          alignItems: isEditing ? "flex-end" : "stretch",
        }}
      >
        {isEditing ? (
          <>
            <Box sx={{ display: "flex", gap: "24px", width: "100%", flexWrap: "wrap" }}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "4px",
                  flex: "1 1 0",
                  minWidth: { xs: "100%", md: 0 },
                  maxWidth: { xs: "100%", md: "400px" },
                }}
              >
                <Typography
                  sx={{
                    fontFamily: "Rethink Sans",
                    fontWeight: 400,
                    fontSize: "14px",
                    lineHeight: "143%",
                    letterSpacing: "0.17px",
                    color: "rgba(0,0,0,0.6)",
                  }}
                >
                  First Name
                </Typography>
                <TextField
                  fullWidth
                  value={draft.firstName}
                  onChange={(e) => setDraft((p) => ({ ...p, firstName: e.target.value }))}
                  sx={inputSx}
                />
              </Box>

              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "4px",
                  flex: "1 1 0",
                  minWidth: { xs: "100%", md: 0 },
                  maxWidth: { xs: "100%", md: "400px" },
                }}
              >
                <Typography
                  sx={{
                    fontFamily: "Rethink Sans",
                    fontWeight: 400,
                    fontSize: "14px",
                    lineHeight: "143%",
                    letterSpacing: "0.17px",
                    color: "rgba(0,0,0,0.6)",
                  }}
                >
                  Last Name
                </Typography>
                <TextField
                  fullWidth
                  value={draft.lastName}
                  onChange={(e) => setDraft((p) => ({ ...p, lastName: e.target.value }))}
                  sx={inputSx}
                />
              </Box>
            </Box>

            <Box sx={{ display: "flex", gap: "24px", width: "100%", flexWrap: "wrap" }}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "4px",
                  flex: "1 1 0",
                  minWidth: { xs: "100%", md: 0 },
                  maxWidth: { xs: "100%", md: "400px" },
                }}
              >
                <Typography
                  sx={{
                    fontFamily: "Rethink Sans",
                    fontWeight: 400,
                    fontSize: "14px",
                    lineHeight: "143%",
                    letterSpacing: "0.17px",
                    color: "rgba(0,0,0,0.6)",
                  }}
                >
                  Email Address
                </Typography>
                <TextField
                  fullWidth
                  value={draft.email}
                  onChange={(e) => setDraft((p) => ({ ...p, email: e.target.value }))}
                  sx={inputSx}
                />
              </Box>

              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "4px",
                  flex: "1 1 0",
                  minWidth: { xs: "100%", md: 0 },
                  maxWidth: { xs: "100%", md: "400px" },
                }}
              >
                <Typography
                  sx={{
                    fontFamily: "Rethink Sans",
                    fontWeight: 400,
                    fontSize: "14px",
                    lineHeight: "143%",
                    letterSpacing: "0.17px",
                    color: "rgba(0,0,0,0.6)",
                  }}
                >
                  Address
                </Typography>
                <TextField
                  fullWidth
                  value={draft.address1}
                  onChange={(e) => setDraft((p) => ({ ...p, address1: e.target.value }))}
                  sx={inputSx}
                />
              </Box>
            </Box>

            <Box sx={{ display: "flex", gap: "24px", width: "100%", flexWrap: "wrap" }}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "4px",
                  flex: "1 1 0",
                  minWidth: { xs: "100%", md: 0 },
                  maxWidth: { xs: "100%", md: "400px" },
                }}
              >
                <Typography
                  sx={{
                    fontFamily: "Rethink Sans",
                    fontWeight: 400,
                    fontSize: "14px",
                    lineHeight: "143%",
                    letterSpacing: "0.17px",
                    color: "rgba(0,0,0,0.6)",
                  }}
                >
                  ZIP Code
                </Typography>
                <TextField
                  fullWidth
                  value={draft.zip}
                  onChange={(e) => setDraft((p) => ({ ...p, zip: e.target.value }))}
                  sx={inputSx}
                />
              </Box>

              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "4px",
                  flex: "1 1 0",
                  minWidth: { xs: "100%", md: 0 },
                  maxWidth: { xs: "100%", md: "400px" },
                }}
              >
                <Typography
                  sx={{
                    fontFamily: "Rethink Sans",
                    fontWeight: 400,
                    fontSize: "14px",
                    lineHeight: "143%",
                    letterSpacing: "0.17px",
                    color: "rgba(0,0,0,0.6)",
                  }}
                >
                  City
                </Typography>
                <TextField
                  fullWidth
                  value={draft.city}
                  onChange={(e) => setDraft((p) => ({ ...p, city: e.target.value }))}
                  sx={inputSx}
                />
              </Box>
            </Box>

            <Box sx={{ display: "flex", flexDirection: "column", gap: "8px", width: "100%" }}>
              <Typography
                sx={{
                  fontFamily: "Rethink Sans",
                  fontWeight: 400,
                  fontSize: "14px",
                  lineHeight: "143%",
                  letterSpacing: "0.17px",
                  color: "rgba(0,0,0,0.6)",
                }}
              >
                Your Photo
              </Typography>

              <Box
                sx={{
                  display: "flex",
                  gap: "16px",
                  alignItems: "center",
                  width: "100%",
                  flexWrap: { xs: "wrap", md: "nowrap" },
                }}
              >
                <Avatar
                  src={photoPreviewUrl || profilePictureSrc || ""}
                  sx={{
                    width: 72,
                    height: 72,
                    bgcolor: "rgba(0,0,0,0.12)",
                    flexShrink: 0,
                  }}
                />

                <ButtonBase
                  onClick={() => photoFilePickerRef.current?.click?.()}
                  sx={{
                    flex: "1 1 0",
                    minWidth: 0,
                    backgroundColor: "#FDFDFD",
                    border: "1px dashed #BDBDBD",
                    borderRadius: "12px",
                    px: "16px",
                    py: "20px",
                  }}
                >
                  <Box
                    sx={{
                      width: "100%",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "16px",
                    }}
                  >
                    <Box
                      sx={{
                        backgroundColor: "#F5F5F5",
                        borderRadius: "999px",
                        p: "10px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <CloudUploadOutlinedIcon
                        sx={{ width: 24, height: 24, color: "rgba(0,0,0,0.54)" }}
                      />
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "12px",
                      }}
                    >
                      <Typography
                        sx={{
                          fontFamily: "Rethink Sans",
                          fontWeight: 400,
                          fontSize: "14px",
                          lineHeight: "143%",
                          letterSpacing: "0.17px",
                          color: "rgba(0,0,0,0.38)",
                          textAlign: "center",
                        }}
                      >
                        {photoFile ? photoFile.name : "Click to upload or drag and drop"}
                      </Typography>
                      <Typography
                        sx={{
                          fontFamily: "Rethink Sans",
                          fontWeight: 400,
                          fontSize: "14px",
                          lineHeight: "143%",
                          letterSpacing: "0.17px",
                          color: "#536DEC",
                          textAlign: "center",
                        }}
                      >
                        SVG, PNG, JPEG OR GIF (max 1080px1200px)
                      </Typography>
                    </Box>
                  </Box>
                </ButtonBase>

                <input
                  ref={photoFilePickerRef}
                  type="file"
                  accept="image/png,image/jpeg,image/gif,image/svg+xml"
                  style={{ display: "none" }}
                  onChange={(e) => {
                    const file = e.target.files?.[0] || null;
                    setPhotoFile(file);
                  }}
                />
              </Box>
            </Box>

            <Box sx={{ display: "flex", gap: "16px", width: "100%", justifyContent: "flex-end" }}>
              <Button
                variant="outlined"
                onClick={cancelEditing}
                disabled={saving}
                sx={{
                  borderRadius: "8px",
                  px: "22px",
                  py: "8px",
                  textTransform: "capitalize",
                  borderColor: "rgba(83,109,236,0.5)",
                  color: "#536DEC",
                  fontFamily: "Rethink Sans",
                  fontWeight: 500,
                  fontSize: "15px",
                  lineHeight: "26px",
                  letterSpacing: "0.46px",
                  "&:hover": {
                    borderColor: "rgba(83,109,236,0.5)",
                    backgroundColor: "rgba(83,109,236,0.04)",
                  },
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={saveChanges}
                disabled={saving}
                sx={{
                  borderRadius: "8px",
                  px: "22px",
                  py: "8px",
                  textTransform: "capitalize",
                  backgroundColor: "#536DEC",
                  fontFamily: "Rethink Sans",
                  fontWeight: 500,
                  fontSize: "15px",
                  lineHeight: "26px",
                  letterSpacing: "0.46px",
                  "&:hover": { backgroundColor: "#4C63D7" },
                }}
              >
                Save
              </Button>
            </Box>
          </>
        ) : (
          <>
            <Box sx={{ display: "flex", gap: "24px", width: "100%", flexWrap: "wrap" }}>
              <Field label="First Name" value={user?.first_name} />
              <Field label="Last Name" value={user?.last_name} />
            </Box>

            <Box sx={{ display: "flex", gap: "24px", width: "100%", flexWrap: "wrap" }}>
              <Field label="Email Address" value={user?.email} />
              <Field label="Address" value={address} />
            </Box>

            <Box sx={{ display: "flex", gap: "24px", width: "100%", flexWrap: "wrap" }}>
              <Field label="ZIP Code" value={zipCode} />
              <Field label="City" value={city} />
            </Box>

            <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              <Typography
                sx={{
                  fontFamily: "Rethink Sans",
                  fontWeight: 400,
                  fontSize: "14px",
                  lineHeight: "143%",
                  letterSpacing: "0.17px",
                  color: "rgba(0,0,0,0.6)",
                }}
              >
                Your Photo
              </Typography>
              <Avatar
                src={profilePictureSrc || ""}
                sx={{
                  width: 72,
                  height: 72,
                  bgcolor: "rgba(0,0,0,0.12)",
                }}
              />
            </Box>
          </>
        )}
      </Box>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={5000}
        onClose={(_, reason) => {
          if (reason === "clickaway") return;
          setSnackbarOpen(false);
        }}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: "100%", fontFamily: "Rethink Sans" }}
        >
          {snackbarMsg}
        </Alert>
      </Snackbar>
    </Card>
  );
}

